import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EducationLevelsGQL,
  InterestQuestionsGQL,
  SearchDegreesForCcafGQL,
  SearchDegreesGQL
} from '@cpd-app/generated';

import { EducationLevel, DegreeTitle, Interest } from '../../models';

/**
 * Where the interests survey's questions are. `interestsSurveyList$` alone
 * could not say: it holds undefined both while the fetch runs and after it
 * has failed (QA.19 U-1).
 */
export type InterestQuestionsStatus = 'loading' | 'loaded' | 'failed';

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private interestsSurveyListSubject = new BehaviorSubject<Interest[]>(
    undefined
  );

  public interestsSurveyList$ = this.interestsSurveyListSubject.asObservable();

  private interestsSurveyStatusSubject =
    new BehaviorSubject<InterestQuestionsStatus>('loading');

  public interestsSurveyStatus$ =
    this.interestsSurveyStatusSubject.asObservable();

  constructor(
    private interestQuestionsGQL: InterestQuestionsGQL,
    private educationLevelsGQL: EducationLevelsGQL,
    private searchDegreesGQL: SearchDegreesGQL,
    private searchDegreesForCcafGQL: SearchDegreesForCcafGQL
  ) {}

  public getEdLevels(): Observable<EducationLevel[]> {
    return this.educationLevelsGQL
      .fetch()
      .pipe(map((result) => result.data.educationLevels as EducationLevel[]));
  }

  public searchDegrees(partialTitle: string): Observable<{ search: DegreeTitle[]; fuzzy: DegreeTitle[] }> {
    return this.searchDegreesGQL.fetch({ searchTerm: partialTitle }).pipe(
      map((results) => ({
        search: (results?.data?.searchDegrees?.search ?? []) as DegreeTitle[],
        fuzzy: (results?.data?.searchDegrees?.fuzzy ?? []) as DegreeTitle[]
      }))
    );
  }

  public searchDegreesForCcaf(partialTitle: string): Observable<{ search: DegreeTitle[]; fuzzy: DegreeTitle[] }> {
    return this.searchDegreesForCcafGQL
      .fetch({ searchTerm: partialTitle })
      .pipe(
        map((results) => ({
          search: (results?.data?.searchDegreesForCCAF?.search ?? []) as DegreeTitle[],
          fuzzy: (results?.data?.searchDegreesForCCAF?.fuzzy ?? []) as DegreeTitle[]
        }))
      );
  }

  /**
   * QA.19 U-1: a failed fetch, or one that comes back without data, is
   * reported as 'failed' rather than thrown inside the stream, where nothing
   * caught it and the survey waited on questions that were never coming. The
   * list keeps whatever it last held, so a page already showing questions is
   * not emptied by a failed retry.
   */
  public getInterestSurveyQuestions(): void {
    this.interestsSurveyStatusSubject.next('loading');

    this.interestQuestionsGQL.fetch().subscribe({
      next: (response) => {
        const list = response?.data?.interestQuestions;
        if (!Array.isArray(list)) {
          this.failInterestQuestions(response?.errors ?? response);
          return;
        }

        const questions: Interest[] = list.map((q) => {
          return {
            interestsId: q.interestsId,
            question: q.question,
            userAnswer: null
          };
        });

        this.interestsSurveyListSubject.next(questions);
        this.interestsSurveyStatusSubject.next('loaded');
      },
      error: (error) => this.failInterestQuestions(error)
    });
  }

  private failInterestQuestions(error: unknown): void {
    console.error('Interests survey questions failed to load', error);
    this.interestsSurveyStatusSubject.next('failed');
  }
}
